<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Klaro Consent -->
    <KlaroConsent />
    
    <!-- HEADER -->
    <header class="text-white">
      <div class="container mx-auto px-4 md:px-8 py-4 md:py-6">
        <div class="flex justify-between items-center">
          <router-link to="/">
            <img src="@/assets/images/logo_header.svg" :alt="t('app.title')" class="h-6 md:h-8" />
          </router-link>
          
          <!-- Desktop Navigation -->
          <div class="hidden lg:flex gap-4 items-center">
            <router-link to="/offers" class="btn-outline-invert">{{ t('app.offers') }}</router-link>
            <router-link v-if="isArticlesEnabled" to="/articles" class="btn-outline-invert">{{ t('articles.title') }}</router-link>
            <router-link to="/add-offer" class="btn">{{ t('app.addOffer') }}</router-link>
            <router-link v-if="auth.isAuthenticated" to="/favorites" class="btn-outline-invert flex items-center">
              <svg class="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              {{ t('userMenu.favorites') }}
            </router-link>
            <router-link 
              v-if="comparison.hasComparisonOffers" 
              to="/comparison" 
              class="btn-outline-invert flex items-center"
            >
              <svg class="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
              </svg>
              {{ t('app.compare') }} ({{ comparison.comparisonCount }})
            </router-link>
            <UserMenu />
          </div>
          
          <!-- Tablet User Menu -->
          <div class="hidden md:flex lg:hidden items-center">
            <UserMenu />
          </div>
          
          <!-- Mobile Menu Button -->
          <div class="md:hidden flex items-center gap-2">
            <UserMenu />
            <button 
              @click="isMobileMenuOpen = !isMobileMenuOpen"
              class="p-2 text-[#06474D] hover:bg-[#06474D] hover:bg-opacity-10 rounded-[0.5rem] transition border border-[#06474D] border-opacity-30"
              aria-label="t('accessibility.toggleMobileMenu')"
            >
              <svg 
                class="w-6 h-6" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  v-if="!isMobileMenuOpen"
                  stroke-linecap="round" 
                  stroke-linejoin="round" 
                  stroke-width="2" 
                  d="M4 6h16M4 12h16M4 18h16"
                />
                <path 
                  v-else
                  stroke-linecap="round" 
                  stroke-linejoin="round" 
                  stroke-width="2" 
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
        
        <!-- Mobile Add Offer Button (Homepage Only) -->
        <div v-if="route.name === 'Home'" class="md:hidden mt-3 pb-3">
          <router-link 
            to="/add-offer" 
            class="block w-full text-center btn text-lg py-3 px-6"
          >
            {{ t('app.addOffer') }}
          </router-link>
        </div>
        
        <!-- Tablet Navigation Buttons -->
        <div class="hidden md:block lg:hidden mt-3 pb-2 border-t border-white border-opacity-20 pt-3">
          <div class="flex justify-center gap-4 items-center">
            <a href="/offers" class="rounded-lg font-bold text-base px-4 py-2 bg-white text-[#06474D] border-2 border-[#06474D] hover:bg-[#E3C47A] hover:text-[#06474D] transition-colors">
              {{ t('app.offers') }}
            </a>
            <a v-if="isArticlesEnabled" href="/articles" class="rounded-lg font-bold text-base px-4 py-2 bg-white text-[#06474D] border-2 border-[#06474D] hover:bg-[#E3C47A] hover:text-[#06474D] transition-colors">
              {{ t('articles.title') }}
            </a>
            <a href="/add-offer" class="rounded-lg font-bold text-base px-4 py-2 bg-[#06474D] text-white hover:bg-[#E3C47A] hover:text-[#06474D] transition-colors">
              {{ t('app.addOffer') }}
            </a>
            <a v-if="auth.isAuthenticated" href="/favorites" class="rounded-lg font-bold text-base px-4 py-2 bg-white text-[#06474D] border-2 border-[#06474D] hover:bg-[#E3C47A] hover:text-[#06474D] transition-colors flex items-center">
              <svg class="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              Ulubione
            </a>
            <a v-if="comparison.hasComparisonOffers" href="/comparison" class="rounded-lg font-bold text-base px-4 py-2 bg-white text-[#06474D] border-2 border-[#06474D] hover:bg-[#E3C47A] hover:text-[#06474D] transition-colors flex items-center">
              <svg class="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
              </svg>
              Porównaj ({{ comparison.comparisonCount }})
            </a>
          </div>
        </div>
        
        <!-- Mobile Navigation Menu -->
        <div 
          v-show="isMobileMenuOpen" 
          class="md:hidden mt-4 pb-4 border-t border-white border-opacity-20"
        >
          <div class="flex flex-col gap-3 pt-4">
            <router-link 
              to="/offers" 
              class="btn-outline-invert text-center"
              @click="isMobileMenuOpen = false"
            >
              {{ t('app.offers') }}
            </router-link>
            <router-link 
              v-if="isArticlesEnabled"
              to="/articles" 
              class="btn-outline-invert text-center"
              @click="isMobileMenuOpen = false"
            >
              {{ t('articles.title') }}
            </router-link>
            <router-link
              v-if="route.name !== 'home'"
              to="/add-offer" 
              class="btn text-center"
              @click="isMobileMenuOpen = false"
            >
              {{ t('app.addOffer') }}
            </router-link>
            <router-link
              v-if="auth.isAuthenticated"
              to="/favorites" 
              class="btn-outline-invert text-center flex items-center justify-center"
              @click="isMobileMenuOpen = false"
            >
              <svg class="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              {{ t('userMenu.favorites') }}
            </router-link>
            <router-link
              v-if="comparison.hasComparisonOffers"
              to="/comparison" 
              class="btn-outline-invert text-center flex items-center justify-center"
              @click="isMobileMenuOpen = false"
            >
              <svg class="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
              </svg>
              {{ t('app.compare') }} ({{ comparison.comparisonCount }})
            </router-link>
          </div>
        </div>
      </div>
    </header>
    <main>
      <template v-if="route.name === 'home'">
        <router-view></router-view>
      </template>
      <template v-else>
        <div class="footer-bg min-h-screen">
          <router-view></router-view>
        </div>
      </template>
    </main>
    <!-- FOOTER -->
    <footer class="bg-[#06474D] text-white pt-10 pb-4 relative">
      <div class="container mx-auto px-8">
        <div class="grid grid-cols-1 md:grid-cols-5 gap-8 items-start">
          <!-- Logo i opis -->
          <div class="flex flex-col items-start">
            <img src="@/assets/images/logo_stopka.svg" :alt="t('app.footer.title')" class="h-12 mb-2" />
            <span class="text-xs mt-2">{{ t('app.footer.description') }}</span>
          </div>
          <!-- Szybkie linki -->
          <div>
            <div class="font-bold mb-2 text-[#E3C47A] uppercase">{{ t('app.footer.quickLinks') }}</div>
            <ul class="mt-2 space-y-1 text-sm">
              <li><router-link to="/offers" class="hover:underline">{{ t('app.footer.allOffers') }}</router-link></li>
              <li v-if="isArticlesEnabled"><router-link to="/articles" class="hover:underline">{{ t('articles.title') }}</router-link></li>
              <li><router-link to="/add-offer" class="hover:underline">{{ t('app.footer.addOffer') }}</router-link></li>
              <li><router-link to="/about" class="hover:underline">{{ t('app.footer.about') }}</router-link></li>
              <li><router-link to="/faq" class="hover:underline">{{ t('app.footer.faq') }}</router-link></li>
              <li><router-link to="/privacy-policy" class="hover:underline">{{ t('app.footer.privacy') }}</router-link></li>
              <li><router-link to="/terms-of-service" class="hover:underline">{{ t('app.footer.rules') }}</router-link></li>
              <li><router-link to="/pricing" class="hover:underline">{{ t('app.footer.pricing') }}</router-link></li>
              <li><router-link to="/partnership" class="hover:underline">{{ t('app.footer.partnership') }}</router-link></li>
              <li><router-link to="/wiki" class="hover:underline">{{ t('app.footer.wiki') }}</router-link></li>
              <li><button @click="openPrivacySettings" class="hover:underline text-left">{{ t('app.footer.privacySettings') }}</button></li>
            </ul>
          </div>
          <!-- Kontakt -->
          <div>
            <div class="font-bold mb-2 text-[#E3C47A] uppercase">{{ t('app.footer.contact') }}</div>
            <ul class="mt-2 space-y-1 text-sm">
              <li>{{ t('app.footer.email', { address: 'kontakt@gielda4x4.pl' }) }}</li>
              <!-- <li>{{ t('app.footer.phone') }}</li> -->
            </ul>
             <!-- Przycisk "Postaw mi kawę" w prawym dolnym rogu -->
            <div class="mt-10">
              <a 
                href="https://suppi.pl/gielda4x4" 
                target="_blank" 
                rel="noopener noreferrer"
                class="bg-[#E3C47A] text-[#06474D] px-8 py-3 rounded-lg font-semibold hover:bg-[#D4B56A] transition-colors duration-200 flex items-center justify-center"
              >
                <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clip-rule="evenodd"></path>
                </svg>
                {{ t('about.buyCoffee') }}
                <span class="ml-2">☕</span>
              </a>
            </div>
          </div>
          <!-- Facebook Community -->
          <div>
            <div class="font-bold mb-2 text-[#E3C47A] uppercase">{{ t('app.footer.joinCommunity') }}</div>
            <div class="mt-2">
              <p class="text-sm mb-3">{{ t('app.footer.facebookGroup') }}</p>
              <a 
                href="https://www.facebook.com/groups/gielda4x4pl/"
                target="_blank"
                rel="noopener noreferrer"
                class="bg-[#3b5998] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#2d4373] transition-colors duration-200 flex items-center justify-center w-full"
              >
                <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                {{ t('app.footer.joinGroup') }}
              </a>
            </div>
          </div>
          <!-- Social -->
           <div class="flex flex-col items-end">
            <div class="font-bold mb-2 text-[#E3C47A] uppercase">{{ t('app.footer.followUs') }}</div>
            <div class="flex gap-4 mt-2">
              <a href="https://www.facebook.com/profile.php?id=61574095780772" target="_blank" :aria-label="t('accessibility.facebook')" class="w-8 h-8 bg-white bg-opacity-20 rounded-full p-1 flex items-center justify-center">
                <svg width="32" height="31" viewBox="0 0 32 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M0.5 15.5865C0.5 23.2939 6.09679 29.7006 13.4167 31V19.8051H9.54167V15.5H13.4167V12.0551C13.4167 8.18013 15.9135 6.02821 19.4449 6.02821C20.5635 6.02821 21.7699 6.2 22.8885 6.37179V10.3333H20.9083C19.0135 10.3333 18.5833 11.2801 18.5833 12.4865V15.5H22.7167L22.0282 19.8051H18.5833V31C25.9032 29.7006 31.5 23.2939 31.5 15.5865C31.5 7.01375 24.525 0 16 0C7.475 0 0.5 7.01375 0.5 15.5865Z" fill="white"/>
                </svg>
              </a>
              <a href="https://www.instagram.com/gielda4x4" target="_blank" :aria-label="t('accessibility.instagram')" class="w-8 h-8 bg-white bg-opacity-20 rounded-full p-1 flex items-center justify-center">
                <svg width="32" height="33" viewBox="0 0 32 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20.9502 16.5C20.9502 17.8129 20.4286 19.0719 19.5003 20.0002C18.572 20.9285 17.313 21.45 16.0002 21.45C14.6873 21.45 13.4283 20.9285 12.5 20.0002C11.5717 19.0719 11.0502 17.8129 11.0502 16.5C11.0513 16.2173 11.0782 15.9423 11.131 15.675H9.40016V22.2701C9.40016 22.7288 9.77141 23.1 10.2301 23.1H21.7719C21.9917 23.0996 22.2024 23.012 22.3577 22.8564C22.5129 22.7008 22.6002 22.4899 22.6002 22.2701V15.675H20.8693C20.9221 15.9423 20.9491 16.2173 20.9502 16.5ZM16.0002 19.8C16.4336 19.7999 16.8628 19.7144 17.2633 19.5485C17.6637 19.3825 18.0275 19.1392 18.334 18.8327C18.6404 18.5261 18.8834 18.1621 19.0492 17.7616C19.215 17.3611 19.3003 16.9319 19.3002 16.4984C19.3 16.0649 19.2146 15.6357 19.0486 15.2353C18.8826 14.8348 18.6394 14.471 18.3328 14.1646C18.0262 13.8582 17.6623 13.6151 17.2617 13.4493C16.8612 13.2835 16.432 13.1983 15.9985 13.1984C15.1231 13.1986 14.2836 13.5466 13.6647 14.1658C13.0458 14.7849 12.6983 15.6246 12.6985 16.5C12.6987 17.3755 13.0467 18.215 13.6659 18.8338C14.2851 19.4527 15.1247 19.8003 16.0002 19.8ZM19.9602 13.035H21.9385C22.0036 13.0353 22.0682 13.0226 22.1284 12.9978C22.1887 12.9731 22.2435 12.9366 22.2896 12.8906C22.3357 12.8446 22.3723 12.79 22.3973 12.7298C22.4223 12.6697 22.4352 12.6052 22.4352 12.54V10.5617C22.4354 10.4964 22.4227 10.4317 22.3978 10.3714C22.3729 10.311 22.3363 10.2562 22.2902 10.21C22.244 10.1639 22.1892 10.1273 22.1288 10.1024C22.0685 10.0775 22.0038 10.0648 21.9385 10.065H19.9602C19.8949 10.0648 19.8302 10.0775 19.7698 10.1024C19.7095 10.1273 19.6546 10.1639 19.6085 10.21C19.5623 10.2562 19.5257 10.311 19.5009 10.3714C19.476 10.4317 19.4633 10.4964 19.4635 10.5617V12.54C19.4652 12.8123 19.6879 13.035 19.9602 13.035ZM16.0002 0.660034C11.7991 0.660034 7.77016 2.32889 4.79958 5.29946C1.82901 8.27004 0.160156 12.299 0.160156 16.5C0.160156 20.7011 1.82901 24.73 4.79958 27.7006C7.77016 30.6712 11.7991 32.34 16.0002 32.34C18.0803 32.34 20.1401 31.9303 22.0619 31.1343C23.9837 30.3383 25.7298 29.1715 27.2007 27.7006C28.6716 26.2297 29.8384 24.4835 30.6344 22.5617C31.4304 20.6399 31.8402 18.5802 31.8402 16.5C31.8402 14.4199 31.4304 12.3601 30.6344 10.4383C29.8384 8.51653 28.6716 6.77034 27.2007 5.29946C25.7298 3.82858 23.9837 2.66182 22.0619 1.86578C20.1401 1.06975 18.0803 0.660034 16.0002 0.660034ZM24.2502 22.9169C24.2502 23.925 23.4252 24.75 22.417 24.75H9.58331C8.57516 24.75 7.75016 23.925 7.75016 22.9169V10.0832C7.75016 9.07503 8.57516 8.25003 9.58331 8.25003H22.417C23.4252 8.25003 24.2502 9.07503 24.2502 10.0832V22.9169Z" fill="white"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
        <div class="text-center text-xs mt-8">{{ t('app.footer.copyright') }}</div>
      </div>
    </footer>
    
    <!-- Global Notification -->
    <GlobalNotification
      ref="globalNotification"
      :type="notificationData.type"
      :title="notificationData.title"
      :message="notificationData.message"
      :duration="notificationData.duration"
      :auto-close="true"
      :show-close-button="true"
    />
  </div>
</template>

<script setup>
import KlaroConsent from '@/components/KlaroConsent.vue'
import UserMenu from '@/components/UserMenu.vue'
import GlobalNotification from '@/components/GlobalNotification.vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useComparisonStore } from '@/stores/comparison'
import { useMessagesStore } from '@/stores/messages'
import { useGoogleAnalytics } from '@/composables/useGoogleAnalytics'
import { useMetaPixel } from '@/composables/useMetaPixel'
import { useAnalyticsLoader } from '@/composables/useAnalyticsLoader'
import { useMetaPixelLoader } from '@/composables/useMetaPixelLoader'
import { useFeatureFlags } from '@/composables/useFeatureFlags'
import { ref, onMounted, watch, onBeforeMount } from 'vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const comparison = useComparisonStore()
const messagesStore = useMessagesStore()
const { isArticlesEnabled } = useFeatureFlags()
const isMobileMenuOpen = ref(false)

// Google Analytics
const { trackLogin, trackRegistration } = useGoogleAnalytics()

// Meta Pixel
const { trackLogin: trackMetaPixelLogin, trackRegistration: trackMetaPixelRegistration } = useMetaPixel()


// Analytics Loader
const { loadGoogleAnalyticsScripts, checkGtagAvailability } = useAnalyticsLoader()

// Meta Pixel Loader
const { loadMetaPixelScripts, checkFbqAvailability } = useMetaPixelLoader()


// Global notification system
const globalNotification = ref(null)
const notificationData = ref({
  type: 'info',
  title: '',
  message: '',
  duration: 3000
})

function showGlobalNotification(data) {
  notificationData.value = { ...notificationData.value, ...data }
  globalNotification.value?.show()
}

function openPrivacySettings() {
  if (window.klaro && typeof window.klaro.show === 'function') {
    window.klaro.show()
  }
}

onMounted(() => {
  // Listen for global notification events
  window.addEventListener('show-notification', (event) => {
    showGlobalNotification(event.detail)
  })
  
  // Listen for auth logout events
  window.addEventListener('auth-logout', (event) => {
    if (route.path !== '/login') {
      router.push('/login')
      // Wyświetl powiadomienie o wylogowaniu
      showGlobalNotification({
        type: 'warning',
        title: 'Sesja wygasła',
        message: 'Zostałeś automatycznie wylogowany. Zaloguj się ponownie.',
        duration: 5000
      })
    }
  })

  // Listen for auth events to track
  window.addEventListener('auth-login', (event) => {
    trackLogin(event.detail?.method || 'email')
  })

  window.addEventListener('auth-register', (event) => {
    trackRegistration(event.detail?.method || 'email')
  })
  
  // Initialize messages store if authenticated
  if (auth.isAuthenticated) {
    messagesStore.fetchUnreadCount()
  }
})

// Watch for authentication changes
watch(() => auth.isAuthenticated, async (isAuthenticated) => {
  if (isAuthenticated) {
    await messagesStore.fetchUnreadCount()
    
    // Identify user in Microsoft Clarity
    if (window.clarity && auth.user?.id) {
      window.clarity('identify', auth.user.id.toString())
    }
  } else {
    messagesStore.reset()
    
    // Clear user identification in Microsoft Clarity
    if (window.clarity) {
      window.clarity('identify', null)
    }
  }
})

// Watch for route changes to check messages
watch(() => route.path, async (newPath) => {
  if (auth.isAuthenticated) {
    await messagesStore.fetchUnreadCount()
  }
})
</script>